// ============================================================
// DocumentRow.tsx
// Shared row component for displaying a DocumentSearchResult in a
// list. Used by DocumentSearchView's main results list and by
// ClientSearchView's per-client documents section. Pure
// presentational — no store reads, no network calls. Parent
// supplies the open-case and download handlers plus the
// per-row downloading flag.
//
// Module-scope helpers (formatFileSize, formatUploadedAt) are
// exported alongside the row because they're tightly coupled
// to the rendering. DownloadIcon is exported because FileList
// on Case Detail also uses it for visual consistency with the
// row's download button. TrashIcon stays internal — if any
// future view wants a delete-icon button outside this row
// component, promote it then.
// ============================================================
import EsignedTag, { ProofTag } from './EsignedTag'
import React from 'react'
import { DocumentSearchResult } from '../../types/documentSearch.types'

// ── DOWNLOAD ICON ──────────────────────────────────────────
// Lucide-style download glyph (downward arrow into a tray).
// currentColor + 18×18 so it sits cleanly inside an icon button
// with the rest of the row's typography.
export const DownloadIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

// ── OPEN / PREVIEW ICON ────────────────────────────────────
// Lucide-style "external link" glyph — signals "open in a new tab".
// Same 18×18 + currentColor as the other row icons. Exported so
// FileList reuses it for visual consistency.
export const OpenIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

// ── TRASH ICON ─────────────────────────────────────────────
// Lucide-style trash glyph (lid + bin + two vertical handles).
// Matches DownloadIcon's 18×18 + currentColor shape so the two
// icon buttons sit at identical visual weight.
const TrashIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
)

// ── FORMATTING HELPERS ─────────────────────────────────────
// Compact bytes → human-readable. KB to 0dp, MB to 1dp. Bytes
// <1KB show the raw count so the unit doesn't lie (a 200-byte
// file isn't "0.2 KB", it's "200 B").
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Short en-GB date — "5 Jun 2026" style. Compact enough for the
// right column without losing the year (uploaded files can be
// years old).
export const formatUploadedAt = (iso: string): string => {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// ── DOCUMENT ROW ───────────────────────────────────────────
// Three siblings inside a wrapping <div>:
//   1. Text area — a <button> that opens the linked case when
//      result.caseId is set, OR a non-clickable <div> with a
//      "Client-level" pill when caseId is null.
//   2. Download icon button — fires onDownload(result).
//   3. Delete icon button — fires onDelete(result), red-hover.
//      Parent shows a ConfirmDialogue before firing the actual
//      delete API call; this prop is purely the trigger.
// Keeping the siblings flat — not nesting the action buttons
// inside the text area — means action clicks don't bubble to
// the open-case handler, no stopPropagation gymnastics needed.
// The outer <div> carries the border + hover-border-shift so the
// whole card reads as a unit; hover/focus-within ensures the
// border shifts whichever sub-button the user is interacting
// with. Left borders on the action buttons visually separate
// the three actions so the row reads as "open OR download OR
// delete".
//
// onOpenCase stays strictly (caseId: string) => void — it's only
// invoked from the truthy branch where result.caseId is provably
// non-null. The `!` assertion at the call site is safe and the
// type guard is the surrounding `result.caseId !== null` check.
export interface DocumentRowProps {
  result: DocumentSearchResult
  onOpenCase: (caseId: string) => void
  // Open the FILE — previews previewable types in a new tab, downloads
  // the rest (parent decides via mime). Distinct from onOpenCase, which
  // navigates to the linked case.
  onOpen: (result: DocumentSearchResult) => void
  onDownload: (result: DocumentSearchResult) => void
  downloading: boolean
  onDelete: (result: DocumentSearchResult) => void
  deleting: boolean
}
const DocumentRow: React.FC<DocumentRowProps> = ({ result, onOpenCase, onOpen, onDownload, downloading, onDelete, deleting }) => (
  <div className="bg-casevo-surface border border-casevo-border rounded-lg hover:border-casevo-mid focus-within:border-casevo-mid transition-colors flex items-stretch">
    {result.caseId !== null ? (
      <button
        type="button"
        onClick={() => onOpenCase(result.caseId!)}
        className="flex-1 text-left px-4 py-3 flex justify-between items-start gap-3 focus:outline-none casevo-focus min-w-0"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm text-casevo-forest font-medium truncate">{result.filename}</p>
          <p className="text-xs text-casevo-mid truncate">{result.caseLabel}</p>
          {result.esigned && <EsignedTag esigned={result.esigned} className="mt-0.5" />}
          {result.proofFor && <ProofTag label={result.proofFor} className="mt-0.5" />}
        </div>
        <div className="text-xs text-casevo-mid shrink-0 mt-0.5 text-right">
          <p>{formatFileSize(result.sizeBytes)}</p>
          <p>{formatUploadedAt(result.uploadedAt)}</p>
        </div>
      </button>
    ) : (
      <div className="flex-1 text-left px-4 py-3 flex justify-between items-start gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-casevo-forest font-medium truncate">{result.filename}</p>
          <span className="inline-block mt-0.5 text-xs px-2 py-0.5 rounded-full bg-casevo-mint text-casevo-brand">
            Client-level
          </span>
        </div>
        <div className="text-xs text-casevo-mid shrink-0 mt-0.5 text-right">
          <p>{formatFileSize(result.sizeBytes)}</p>
          <p>{formatUploadedAt(result.uploadedAt)}</p>
        </div>
      </div>
    )}
    <button
      type="button"
      onClick={() => onOpen(result)}
      disabled={downloading}
      title="Open"
      aria-label={`Open ${result.filename}`}
      className="px-3 flex items-center justify-center text-casevo-mid hover:text-casevo-forest border-l border-casevo-border focus:outline-none focus-visible:text-casevo-forest disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      <OpenIcon />
    </button>
    <button
      type="button"
      onClick={() => onDownload(result)}
      disabled={downloading}
      title="Download"
      aria-label={`Download ${result.filename}`}
      className="px-3 flex items-center justify-center text-casevo-mid hover:text-casevo-forest border-l border-casevo-border focus:outline-none focus-visible:text-casevo-forest disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      <DownloadIcon />
    </button>
    {/* The delivered bill PDF can't be deleted (server 409) — no Delete for it.
        (E-sign fixes B) Nor an e-signed copy. */}
    {!result.deliveredBillPdf && !result.esigned && !result.proofFor && <button
      type="button"
      onClick={() => onDelete(result)}
      disabled={deleting}
      title="Delete"
      aria-label={`Delete ${result.filename}`}
      className="px-3 flex items-center justify-center text-casevo-mid hover:text-casevo-danger border-l border-casevo-border focus:outline-none focus-visible:text-casevo-danger disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      <TrashIcon />
    </button>}
  </div>
)
export default DocumentRow
